/**
 * 電子書籍変換ダイアログ
 * SSE進捗表示付きの変換UI
 */

import { FileType2, Loader2 } from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'

import { streamEbookConversion } from '@/api/services/ebook'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Progress } from '@/components/ui/progress'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import { cn } from '@/lib/utils'
import {
  ConversionOptions,
  ConversionProgressEvent,
  ConversionStatus,
  EbookInputFormat,
  EbookOutputFormat,
  EPUBOptions,
  getAvailableOutputFormats,
  PAPER_SIZE_OPTIONS,
  type PaperSizeValue,
  PDFOptions,
} from '@/types/ebook-conversion'

type EbookConversionDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  episodeId: number
  episodeTitle: string
  inputFormat: EbookInputFormat
  productTitle?: string
  /** 変換完了時のコールバック */
  onComplete?: () => void
}

export const EbookConversionDialog = ({
  open,
  onOpenChange,
  episodeId,
  episodeTitle,
  inputFormat,
  productTitle,
  onComplete,
}: EbookConversionDialogProps) => {
  // 利用可能な出力フォーマット
  const availableOutputFormats = getAvailableOutputFormats(inputFormat)

  // 状態管理
  const [outputFormat, setOutputFormat] = useState<EbookOutputFormat>(availableOutputFormats[0] || 'pdf')
  const [isConverting, setIsConverting] = useState(false)
  const [progress, setProgress] = useState(0)
  const [statusMessage, setStatusMessage] = useState('')
  const [status, setStatus] = useState<ConversionStatus | null>(null)

  // カスタム名
  const [customName, setCustomName] = useState('')

  // カスタマイズトグル
  const [customizeEnabled, setCustomizeEnabled] = useState(false)

  // オプション
  const [pdfOptions, setPdfOptions] = useState<PDFOptions>({})
  const [epubOptions, setEpubOptions] = useState<EPUBOptions>({})

  // AbortController参照
  const abortControllerRef = useRef<AbortController | null>(null)

  // ダイアログが開いたときに状態リセット
  useEffect(() => {
    if (open) {
      // 状態リセット
      setIsConverting(false)
      setProgress(0)
      setStatusMessage('')
      setStatus(null)
      setCustomName('')
    }
  }, [open])

  // custom_size パース/構築ヘルパー
  const parseCustomSize = (cs: string | undefined): [string, string] => {
    const parts = cs?.split('x') ?? []
    return [parts[0] ?? '', parts[1] ?? '']
  }
  const buildCustomSize = (w: string, h: string): string | undefined => (w && h ? `${w}x${h}` : undefined)

  // クリーンアップ
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
    }
  }, [])

  // 変換開始
  const handleConvert = useCallback(async () => {
    setIsConverting(true)
    setProgress(0)
    setStatus('starting')
    setStatusMessage('変換を開始しています...')

    // オプションを構築
    const buildPdfOptions = (): PDFOptions | undefined => {
      if (outputFormat !== 'pdf') return undefined
      const opts = { ...pdfOptions }
      // UI専用値（none / custom）はAPIに送らない。cover はMPSで処理。
      if (!opts.paper_size || opts.paper_size === 'none' || opts.paper_size === 'custom') {
        delete opts.paper_size
      }
      return opts
    }

    const options: ConversionOptions = {
      pdf: buildPdfOptions(),
      epub: outputFormat === 'epub' ? epubOptions : undefined,
    }

    // AbortController作成
    const abortController = new AbortController()
    abortControllerRef.current = abortController

    try {
      await streamEbookConversion(
        episodeId,
        outputFormat as 'pdf' | 'epub',
        options,
        (data: ConversionProgressEvent) => {
          setStatus(data.status)
          setStatusMessage(data.message)

          if (data.progress !== undefined) {
            setProgress(data.progress)
          }

          if (data.status === 'completed') {
            setIsConverting(false)
            setProgress(100)
            onComplete?.()
            // 少し遅延してダイアログを閉じる
            setTimeout(() => {
              onOpenChange(false)
            }, 1500)
          }

          if (data.status === 'error') {
            setIsConverting(false)
            toast.error(`変換エラー: ${data.message}`)
          }
        },
        (error: Error) => {
          setIsConverting(false)
          setStatus('error')
          setStatusMessage('接続エラーが発生しました')
          toast.error(`変換サービスへの接続に失敗しました: ${error.message}`)
        },
        abortController.signal,
        customName || undefined,
      )
    } catch (error) {
      setIsConverting(false)
      setStatus('error')
      setStatusMessage('変換の開始に失敗しました')
      toast.error('変換の開始に失敗しました')
      console.error('Conversion error:', error)
    }
  }, [episodeId, outputFormat, pdfOptions, epubOptions, customName, onComplete, onOpenChange])

  // キャンセル
  const handleCancel = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
      abortControllerRef.current = null
    }
    setIsConverting(false)
    setStatus(null)
    setProgress(0)
    setStatusMessage('')
  }, [])

  return (
    <Dialog open={open} onOpenChange={isConverting ? undefined : onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileType2 size={20} />
            電子書籍変換
          </DialogTitle>
        </DialogHeader>

        {/* エピソード情報 */}
        <div className="text-sm text-muted-foreground mb-4">
          <p>
            <span className="font-medium text-foreground">
              {productTitle && `${productTitle} - `}
              {episodeTitle}
            </span>
          </p>
          <p className="mt-1">
            入力形式: <span className="font-medium">{inputFormat.toUpperCase()}</span>
          </p>
        </div>

        {/* 変換中の進捗表示 */}
        {isConverting && (
          <div className="space-y-3 p-4 bg-muted/50 rounded-lg">
            <div className="flex items-center gap-2">
              <Loader2 className="animate-spin" size={16} />
              <span className="text-sm font-medium">変換中...</span>
            </div>
            <Progress value={progress} className="h-2" />
            <p className="text-xs text-muted-foreground">{statusMessage}</p>
          </div>
        )}

        {/* 完了表示 */}
        {status === 'completed' && (
          <div className="p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
            <p className="text-sm text-green-500 font-medium">✓ 変換が完了しました</p>
          </div>
        )}

        {/* 変換設定（変換中でない場合のみ表示） */}
        {!isConverting && status !== 'completed' && (
          <>
            {/* カスタム名 */}
            <div className="space-y-2">
              <Label htmlFor="custom-name">カスタム名</Label>
              <Input
                id="custom-name"
                value={customName}
                onChange={(e) => setCustomName(e.target.value)}
                placeholder="表示名を入力"
              />
            </div>

            {/* 出力フォーマット表示/選択 */}
            <div className="space-y-2">
              <Label>出力フォーマット</Label>
              {availableOutputFormats.length === 1 ? (
                // 選択肢が1つの場合はテキスト表示のみ
                <p className="text-sm font-medium">{availableOutputFormats[0].toUpperCase()}</p>
              ) : (
                // 複数選択肢がある場合（AZW3からの変換時）
                <div className="flex gap-2">
                  {availableOutputFormats.map((format) => (
                    <Button
                      key={format}
                      variant={outputFormat === format ? 'default' : 'outline'}
                      className={cn('flex-1', outputFormat === format && 'bg-blue-600 hover:bg-blue-700')}
                      onClick={() => setOutputFormat(format)}
                    >
                      {format.toUpperCase()}
                    </Button>
                  ))}
                </div>
              )}
            </div>

            {/* カスタマイズトグル */}
            <div className="flex items-center justify-between">
              <Label htmlFor="customize-toggle" className="text-sm font-medium">
                カスタマイズ
              </Label>
              <Switch id="customize-toggle" checked={customizeEnabled} onCheckedChange={setCustomizeEnabled} />
            </div>

            {/* フォーマット別設定 */}
            {customizeEnabled && (
              <>
                <div className="mt-4 space-y-4">
                  <h4 className="text-sm font-medium">{outputFormat === 'pdf' ? 'PDF設定' : 'EPUB設定'}</h4>
                  {outputFormat === 'pdf' && (
                    <>
                      {/* 用紙サイズ */}
                      <div className="space-y-2">
                        <Label>用紙サイズ</Label>
                        <Select
                          value={pdfOptions.paper_size ?? 'none'}
                          onValueChange={(value: string) => {
                            const paperSize = value as PaperSizeValue
                            const newOpts = { ...pdfOptions, paper_size: paperSize === 'none' ? undefined : paperSize }
                            // カスタムサイズ以外を選んだらcustom_sizeをクリア
                            if (value !== 'custom') {
                              delete newOpts.custom_size
                            }
                            setPdfOptions(newOpts)
                          }}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="指定しない" />
                          </SelectTrigger>
                          <SelectContent>
                            {PAPER_SIZE_OPTIONS.map((option) => (
                              <SelectItem key={option.value} value={option.value}>
                                {option.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      {/* カスタムサイズ入力（「カスタムサイズ」選択時のみ） */}
                      {pdfOptions.paper_size === 'custom' && (
                        <div className="space-y-2">
                          <Label className="text-xs text-muted-foreground">カスタムページサイズ (pt)</Label>
                          <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-1">
                              <Label htmlFor="custom-width" className="text-xs">
                                幅
                              </Label>
                              <Input
                                id="custom-width"
                                type="number"
                                min={1}
                                value={parseCustomSize(pdfOptions.custom_size)[0]}
                                onChange={(e) => {
                                  const [, h] = parseCustomSize(pdfOptions.custom_size)
                                  setPdfOptions({ ...pdfOptions, custom_size: buildCustomSize(e.target.value, h) })
                                }}
                                placeholder="595"
                              />
                            </div>
                            <div className="space-y-1">
                              <Label htmlFor="custom-height" className="text-xs">
                                高さ
                              </Label>
                              <Input
                                id="custom-height"
                                type="number"
                                min={1}
                                value={parseCustomSize(pdfOptions.custom_size)[1]}
                                onChange={(e) => {
                                  const [w] = parseCustomSize(pdfOptions.custom_size)
                                  setPdfOptions({ ...pdfOptions, custom_size: buildCustomSize(w, e.target.value) })
                                }}
                                placeholder="842"
                              />
                            </div>
                          </div>
                          {/* カスタムサイズ未入力時の警告 */}
                          {!pdfOptions.custom_size && (
                            <p className="text-xs text-destructive">幅と高さの両方を入力してください</p>
                          )}
                        </div>
                      )}

                      {/* ページ番号 */}
                      <div className="flex items-center justify-between">
                        <Label htmlFor="page-numbers">ページ番号</Label>
                        <Switch
                          id="page-numbers"
                          checked={pdfOptions.page_numbers ?? false}
                          onCheckedChange={(checked) => setPdfOptions({ ...pdfOptions, page_numbers: checked })}
                        />
                      </div>

                      {/* フォントサイズ */}
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-2">
                          <Label htmlFor="font-size">フォントサイズ</Label>
                          <Input
                            id="font-size"
                            type="number"
                            min={6}
                            max={72}
                            value={pdfOptions.default_font_size ?? ''}
                            onChange={(e) =>
                              setPdfOptions({
                                ...pdfOptions,
                                default_font_size: e.target.value ? parseInt(e.target.value) : undefined,
                              })
                            }
                            placeholder="12"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="mono-font-size">等幅フォントサイズ</Label>
                          <Input
                            id="mono-font-size"
                            type="number"
                            min={6}
                            max={72}
                            value={pdfOptions.mono_font_size ?? ''}
                            onChange={(e) =>
                              setPdfOptions({
                                ...pdfOptions,
                                mono_font_size: e.target.value ? parseInt(e.target.value) : undefined,
                              })
                            }
                            placeholder="10"
                          />
                        </div>
                      </div>

                      {/* フォントファミリー */}
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-2">
                          <Label htmlFor="serif-family">セリフフォント</Label>
                          <Input
                            id="serif-family"
                            value={pdfOptions.serif_family ?? ''}
                            onChange={(e) =>
                              setPdfOptions({ ...pdfOptions, serif_family: e.target.value || undefined })
                            }
                            placeholder="例: Noto Serif JP"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="sans-family">サンセリフフォント</Label>
                          <Input
                            id="sans-family"
                            value={pdfOptions.sans_family ?? ''}
                            onChange={(e) => setPdfOptions({ ...pdfOptions, sans_family: e.target.value || undefined })}
                            placeholder="例: Noto Sans JP"
                          />
                        </div>
                      </div>

                      {/* マージン */}
                      <div className="space-y-2">
                        <Label className="text-xs text-muted-foreground">マージン (pt)</Label>
                        <div className="grid grid-cols-2 gap-3">
                          {(
                            [
                              { id: 'margin-top', label: '上', key: 'margin_top' },
                              { id: 'margin-bottom', label: '下', key: 'margin_bottom' },
                              { id: 'margin-left', label: '左', key: 'margin_left' },
                              { id: 'margin-right', label: '右', key: 'margin_right' },
                            ] as const
                          ).map(({ id, label, key }) => (
                            <div key={id} className="space-y-1">
                              <Label htmlFor={id} className="text-xs">
                                {label}
                              </Label>
                              <Input
                                id={id}
                                type="number"
                                min={0}
                                value={pdfOptions[key] ?? ''}
                                onChange={(e) =>
                                  setPdfOptions({
                                    ...pdfOptions,
                                    [key]: e.target.value ? parseFloat(e.target.value) : undefined,
                                  })
                                }
                                placeholder="72"
                              />
                            </div>
                          ))}
                        </div>
                      </div>
                    </>
                  )}

                  {/* EPUB設定 */}
                  {outputFormat === 'epub' && (
                    <>
                      {/* EPUBバージョン */}
                      <div className="space-y-2">
                        <Label>EPUBバージョン</Label>
                        <Select
                          value={epubOptions.epub_version || '2'}
                          onValueChange={(value: '2' | '3') => setEpubOptions({ ...epubOptions, epub_version: value })}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="2">EPUB 2</SelectItem>
                            <SelectItem value="3">EPUB 3</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      {/* フローサイズ */}
                      <div className="space-y-2">
                        <Label htmlFor="flow-size">フローサイズ (KB)</Label>
                        <Input
                          id="flow-size"
                          type="number"
                          min={0}
                          value={epubOptions.flow_size ?? ''}
                          onChange={(e) =>
                            setEpubOptions({
                              ...epubOptions,
                              flow_size: e.target.value ? parseInt(e.target.value) : undefined,
                            })
                          }
                          placeholder="260"
                        />
                        <p className="text-xs text-muted-foreground">各HTMLファイルの最大サイズ。0で分割なし</p>
                      </div>

                      {/* 目次タイトル */}
                      <div className="space-y-2">
                        <Label htmlFor="toc-title">目次タイトル</Label>
                        <Input
                          id="toc-title"
                          value={epubOptions.toc_title ?? ''}
                          onChange={(e) => setEpubOptions({ ...epubOptions, toc_title: e.target.value || undefined })}
                          placeholder="目次"
                        />
                      </div>

                      {/* カバー設定 */}
                      <div className="flex items-center justify-between">
                        <Label htmlFor="preserve-aspect">カバーアスペクト比を保持</Label>
                        <Switch
                          id="preserve-aspect"
                          checked={epubOptions.preserve_cover_aspect_ratio ?? false}
                          onCheckedChange={(checked) =>
                            setEpubOptions({ ...epubOptions, preserve_cover_aspect_ratio: checked })
                          }
                        />
                      </div>

                      <div className="flex items-center justify-between">
                        <Label htmlFor="no-default-cover">デフォルトカバーを使用しない</Label>
                        <Switch
                          id="no-default-cover"
                          checked={epubOptions.no_default_cover ?? false}
                          onCheckedChange={(checked) => setEpubOptions({ ...epubOptions, no_default_cover: checked })}
                        />
                      </div>

                      <div className="flex items-center justify-between">
                        <Label htmlFor="no-svg-cover">SVGカバーを使用しない</Label>
                        <Switch
                          id="no-svg-cover"
                          checked={epubOptions.no_svg_cover ?? false}
                          onCheckedChange={(checked) => setEpubOptions({ ...epubOptions, no_svg_cover: checked })}
                        />
                      </div>
                    </>
                  )}
                </div>
              </>
            )}
          </>
        )}

        <DialogFooter className="mt-4">
          {isConverting ? (
            <Button variant="outline" onClick={handleCancel}>
              キャンセル
            </Button>
          ) : status === 'completed' ? (
            <Button onClick={() => onOpenChange(false)}>閉じる</Button>
          ) : (
            <>
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                キャンセル
              </Button>
              <Button
                onClick={handleConvert}
                className="bg-blue-600 hover:bg-blue-700"
                disabled={outputFormat === 'pdf' && pdfOptions.paper_size === 'custom' && !pdfOptions.custom_size}
              >
                変換開始
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
